import { useCallback, useEffect, useMemo, useState } from "react";
import { formatUsdFromPcu } from "@/lib/credits-format";
import { chatService } from "../../services/chat-service";
import type { ChatMessage } from "../../types/chat-message";

interface ChatCreditsBadgeProps {
  /** Chat messages used for fallback cost calculation. */
  messages: ChatMessage[];
  /** Chat ID (client-generated UUID, also used as DB primary key). */
  chatId: string;
  /** Team ID when this is a team chat. */
  teamId?: string | null;
  /** Current useChat status ("ready" | "streaming" | "submitted"). */
  status: string;
  /** Number of initial messages loaded on mount (non-zero means existing chat). */
  initialMessagesCount: number;
}

/**
 * Displays the total credits (PCU) consumed by a chat.
 * Fetches aggregated cost from the server, falling back to summing individual message costs.
 */
export function ChatCreditsBadge({
  messages,
  chatId,
  teamId,
  status,
  initialMessagesCount,
}: ChatCreditsBadgeProps) {
  const [chatMetadata, setChatMetadata] = useState<{ cost?: number } | null>(null);

  /**
   * Fetch chat metadata to get aggregated cost.
   * Stable across renders so streaming token deltas don't re-trigger effects.
   */
  const fetchChatMetadata = useCallback(async () => {
    if (!chatId) return;

    try {
      const response = teamId
        ? await chatService.getTeamChatById(teamId, chatId)
        : await chatService.getChatById(chatId);
      if (response.success && response.data?.metadata) {
        setChatMetadata(response.data.metadata);
      }
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
        ?? (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        console.error("Failed to fetch chat metadata:", err);
      }
      setChatMetadata(null);
    }
  }, [chatId, teamId]);

  /** Initial fetch when an existing chat loads. Independent of message stream. */
  useEffect(() => {
    if (initialMessagesCount > 0) {
      fetchChatMetadata();
    }
  }, [chatId, initialMessagesCount, fetchChatMetadata]);

  /**
   * Refresh after an assistant turn completes. Gated on status === "ready"
   * so streaming token deltas don't fire fetches per delta.
   */
  useEffect(() => {
    if (status !== "ready") return;
    if (messages.length === 0) return;
    if (!messages.some((msg) => msg.role === "assistant")) return;

    const timeoutId = setTimeout(fetchChatMetadata, 1500);
    /** Second fetch to catch late-arriving sub-agent billing updates */
    const retryTimeoutId = setTimeout(fetchChatMetadata, 5000);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
    };
  }, [status, messages.length, fetchChatMetadata]);

  const totalCost = useMemo(() => {
    let costInPcu = 0;

    if (chatMetadata?.cost && typeof chatMetadata.cost === "number") {
      costInPcu = chatMetadata.cost;
      return formatUsdFromPcu(costInPcu, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    }

    costInPcu = messages.reduce((acc, msg) => {
      const msgCost = msg.metadata?.cost;
      const numericCost =
        typeof msgCost === "number"
          ? msgCost
          : typeof msgCost === "string"
            ? parseFloat(msgCost) || 0
            : 0;
      return acc + numericCost;
    }, 0);

    return formatUsdFromPcu(costInPcu, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  }, [messages, chatMetadata]);

  return (
    <div className="text-sm text-muted-foreground flex items-center gap-1">
      Total cost:{" "}
      <span className="font-semibold">{totalCost}</span>
    </div>
  );
}
